/**
 * Derive Work Queue analytics from merged real queue items (Task Manager + RCM services).
 * Used by AgenticRCM / Agency shell — no mock fallbacks.
 */

function assignFieldToString(value) {
  if (value == null || value === '') return '';
  if (typeof value === 'string' || typeof value === 'number') return String(value).trim();
  if (typeof value === 'boolean') return String(value);
  if (typeof value === 'object' && typeof value.text === 'string' && value.text) {
    return value.text.trim();
  }
  return '';
}

function assignName(item) {
  if (!item || typeof item !== 'object') return '';
  return (
    assignFieldToString(item.assigned_to_name) ||
    assignFieldToString(item.assignedToName) ||
    assignFieldToString(item.assigned_to) ||
    assignFieldToString(item.assignedTo)
  );
}

/**
 * Same buckets the Inbox Assignee column uses: ai | human | unassigned.
 * Do not treat auto_assigned leftovers as Kai — unassigned rows stay unassigned.
 */
export function resolveQueueAssignmentBucket(item) {
  const atStr = assignFieldToString(item?.assigned_to) || assignFieldToString(item?.assignedTo);
  const nameStr =
    assignFieldToString(item?.assigned_to_name) || assignFieldToString(item?.assignedToName);
  const atLower = atStr.toLowerCase();
  const nameLower = nameStr.toLowerCase();
  const explicit = String(item?.assigned_to_type || '').trim().toLowerCase();
  const looksAi =
    explicit === 'ai' ||
    atLower === 'ai-agent-default' ||
    atLower === 'ai' ||
    atLower === 'kai' ||
    nameLower.includes('kai') ||
    (nameLower.includes('ai') && nameLower.includes('coworker'));
  const assignedToType = explicit || (atStr && looksAi ? 'ai' : null) || (atStr ? 'human' : '');

  if (assignedToType === 'ai' && atStr) return 'ai';
  if (assignedToType === 'human' || nameStr || atStr) return 'human';
  return 'unassigned';
}

export function applyAssignmentTypeFilter(items, assignedToType) {
  const want = String(assignedToType || '').trim().toLowerCase();
  const list = Array.isArray(items) ? items : [];
  if (!want || !['ai', 'human', 'unassigned'].includes(want)) return list;
  return list.filter((item) => resolveQueueAssignmentBucket(item) === want);
}

function statusOf(item) {
  const s = String(item?.status || item?.queue_status || 'pending').toLowerCase().replace(/\s+/g, '_');
  if (['pending', 'assigned', 'in_progress', 'completed', 'cancelled', 'on_hold', 'failed'].includes(s)) {
    return s;
  }
  return 'pending';
}

function isAiAssignee(item) {
  return resolveQueueAssignmentBucket(item) === 'ai';
}

function ageDays(item, now = new Date()) {
  const raw = item?.created_at || item?.updated_at || item?.metadata?.created_at;
  if (!raw) return null;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return null;
  return Math.max(0, Math.floor((now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24)));
}

function isToday(iso, now = new Date()) {
  if (!iso) return false;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  return (
    d.getUTCFullYear() === now.getUTCFullYear() &&
    d.getUTCMonth() === now.getUTCMonth() &&
    d.getUTCDate() === now.getUTCDate()
  );
}

/**
 * @param {object[]} items — merged queue rows
 * @returns {object} analytics snapshot
 */
export function computeMergedQueueAnalytics(items = []) {
  const list = Array.isArray(items) ? items : [];
  const now = new Date();
  const by_status = {
    pending: 0,
    assigned: 0,
    in_progress: 0,
    completed: 0,
    cancelled: 0,
    on_hold: 0,
    other: 0,
  };
  const by_priority = { critical: 0, urgent: 0, high: 0, normal: 0, medium: 0, low: 0 };
  const aging = { '0-7': 0, '8-14': 0, '15-30': 0, '30+': 0, unknown: 0 };
  const by_assignee = {};
  let assigned_to_ai = 0;
  let assigned_to_human = 0;
  let unassigned = 0;
  let overdue = 0;
  let completed_today = 0;
  let open_count = 0;
  let working_count = 0;

  for (const item of list) {
    const st = statusOf(item);
    if (by_status[st] != null) by_status[st] += 1;
    else by_status.other += 1;

    if (st === 'pending' || st === 'assigned') open_count += 1;
    if (st === 'in_progress') working_count += 1;
    if (st === 'completed' && (isToday(item.completed_at, now) || isToday(item.updated_at, now))) {
      completed_today += 1;
    }

    const pri = String(item.priority || 'normal').toLowerCase();
    if (by_priority[pri] != null) by_priority[pri] += 1;
    else by_priority.normal += 1;

    const age = ageDays(item, now);
    if (age == null) aging.unknown += 1;
    else if (age <= 7) aging['0-7'] += 1;
    else if (age <= 14) aging['8-14'] += 1;
    else if (age <= 30) aging['15-30'] += 1;
    else aging['30+'] += 1;

    if (item.due_date) {
      try {
        const due = new Date(item.due_date);
        if (!Number.isNaN(due.getTime()) && due < now && st !== 'completed' && st !== 'cancelled') {
          overdue += 1;
        }
      } catch {
        /* ignore */
      }
    }

    const bucket = resolveQueueAssignmentBucket(item);
    const name = assignName(item);
    if (bucket === 'unassigned') {
      unassigned += 1;
    } else if (bucket === 'ai' || isAiAssignee(item)) {
      assigned_to_ai += 1;
      by_assignee['Kai - AI Coworker'] = (by_assignee['Kai - AI Coworker'] || 0) + 1;
    } else {
      assigned_to_human += 1;
      by_assignee[name || 'Human'] = (by_assignee[name || 'Human'] || 0) + 1;
    }
  }

  const top_assignees = Object.entries(by_assignee)
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 8);

  return {
    total: list.length,
    assigned_to_ai,
    assigned_to_human,
    unassigned,
    overdue,
    open_count,
    working_count,
    closed_count: by_status.completed,
    completed_today,
    by_status,
    by_priority,
    aging,
    top_assignees,
  };
}
